import {
  MessageCircle,
  Phone,
  Users,
  MapPin,
  Clock,
  Truck,
  Sparkles,
} from "lucide-react";
import Reveal from "../components/Reveal";
import LocationSection from "../components/LocationSection";
import { BRAND, waOrder } from "../lib/brand";

const SPICES_IMG = "https://images.pexels.com/photos/2632292/pexels-photo-2632292.jpeg";

const FAQS = [
  {
    q: "How do I place my first order?",
    a: "Just send a WhatsApp message to +91 92679 13203. Tell us your location, preferred meal time (lunch/dinner) and any preferences. We'll confirm and deliver — that simple.",
  },
  {
    q: "What areas do you deliver to?",
    a: "We deliver across Delhi. Delivery is always free. Drop us a message with your pincode and we'll confirm if we cover your area.",
  },
  {
    q: "Can I subscribe monthly?",
    a: "Yes — our monthly tiffin package comes at a reduced daily rate, with flexible pause/resume. Message us for current plans.",
  },
  {
    q: "Do you handle bulk orders or events?",
    a: "Absolutely. Kitty parties, birthdays, anniversaries, pujas — we customise a menu for your occasion and guest count. Share details on WhatsApp for a quote.",
  },
  {
    q: "Is breakfast available?",
    a: "Breakfast is on special order only. Let us know a day in advance and we'll cook something warm and home-style for you.",
  },
];

export default function ContactPage() {
  return (
    <div className="bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative pt-[120px] pb-16 md:pb-20 bg-[#1a2e5a] text-white overflow-hidden" data-testid="contact-header">
        <img
          src={SPICES_IMG}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1530] via-[#1a2e5a]/85 to-[#1a2e5a]" />
        <div className="absolute inset-0 mandala-bg opacity-[0.05]" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
              Place an Order · Ask a Question
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              Talk to <span className="italic text-[#c9a84c]">Archna's Kitchen.</span>
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="font-body text-white/75 text-lg md:text-xl max-w-2xl mx-auto mt-6">
              We accept all orders and queries on WhatsApp. No app, no forms —
              just a friendly message and a reply from a real person.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact methods */}
      <section className="py-16 md:py-24" data-testid="contact-methods">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
          {/* WhatsApp */}
          <Reveal>
            <a
              href={BRAND.whatsappChat}
              target="_blank"
              rel="noopener noreferrer"
              className="card-lift block bg-[#1a2e5a] text-white rounded-3xl p-8 h-full border border-[#c9a84c]/30"
              data-testid="contact-card-whatsapp"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#c9a84c] flex items-center justify-center mb-5">
                <MessageCircle size={24} className="text-[#1a2e5a]" strokeWidth={2.3} />
              </div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a84c] mb-2">
                WhatsApp Chat
              </div>
              <div className="font-display text-2xl font-bold mb-3">Message Us Directly</div>
              <p className="font-body text-white/75 text-sm leading-relaxed mb-5">
                Fastest way to place an order or ask anything. We usually reply
                within minutes during kitchen hours.
              </p>
              <span className="inline-flex items-center gap-2 text-[#c9a84c] font-bold text-sm">
                {BRAND.phoneDisplay} →
              </span>
            </a>
          </Reveal>

          {/* Phone */}
          <Reveal delay={1}>
            <a
              href={`tel:+${BRAND.phoneRaw}`}
              className="card-lift block bg-white text-[#1a2e5a] rounded-3xl p-8 h-full border border-[#c9a84c]/30"
              data-testid="contact-card-phone"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#1a2e5a] flex items-center justify-center mb-5">
                <Phone size={22} className="text-[#c9a84c]" strokeWidth={2.3} />
              </div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a84c] mb-2">
                Call Us
              </div>
              <div className="font-display text-2xl font-bold mb-3">Talk to a Real Person</div>
              <p className="font-body text-slate-600 text-sm leading-relaxed mb-5">
                Prefer a quick call? Dial us and we'll take your order, share
                today's menu, or answer delivery questions.
              </p>
              <span className="inline-flex items-center gap-2 text-[#1a2e5a] font-bold text-sm">
                {BRAND.phoneDisplay} →
              </span>
            </a>
          </Reveal>

          {/* WhatsApp Group */}
          <Reveal delay={2}>
            <a
              href={BRAND.whatsappGroup}
              target="_blank"
              rel="noopener noreferrer"
              className="card-lift block bg-[#0a1530] text-white rounded-3xl p-8 h-full border border-[#c9a84c]/30 relative overflow-hidden"
              data-testid="contact-card-group"
            >
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#c9a84c]/10 blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-[#c9a84c] flex items-center justify-center mb-5">
                  <Users size={22} className="text-[#1a2e5a]" strokeWidth={2.3} />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a84c] mb-2">
                  WhatsApp Group
                </div>
                <div className="font-display text-2xl font-bold mb-3">Daily Menu Updates</div>
                <p className="font-body text-white/75 text-sm leading-relaxed mb-5">
                  Join our WhatsApp community to get the daily menu, early
                  offers, and seasonal specials delivered straight to your
                  chat.
                </p>
                <span className="inline-flex items-center gap-2 text-[#c9a84c] font-bold text-sm">
                  Join the Group →
                </span>
              </div>
            </a>
          </Reveal>
        </div>
      </section>

      {/* Big phone block */}
      <section className="py-12 md:py-16 bg-white border-y border-[#c9a84c]/20" data-testid="contact-big-phone">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6 items-center text-center md:text-left">
          <div className="md:col-span-2">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a84c] font-bold mb-2">
              Any Query · Any Order
            </div>
            <div className="font-display text-[#1a2e5a] text-3xl md:text-4xl font-bold leading-tight mb-3">
              We accept orders and queries on WhatsApp — just send us a message.
            </div>
            <a
              href={`tel:+${BRAND.phoneRaw}`}
              className="font-display text-4xl md:text-5xl text-[#c9a84c] font-bold tracking-wide inline-block"
              data-testid="contact-large-phone"
            >
              {BRAND.phoneDisplay}
            </a>
          </div>
          <div className="flex md:justify-end">
            <a
              href={BRAND.whatsappChat}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
              data-testid="contact-big-whatsapp"
            >
              <MessageCircle size={18} strokeWidth={2.5} />
              Start WhatsApp Chat
            </a>
          </div>
        </div>
      </section>

      {/* Info tiles */}
      <section className="py-16 md:py-20" data-testid="contact-info-tiles">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Truck,
              title: "Free Home Delivery",
              desc: "All across Delhi, piping hot and on time — at no extra charge.",
            },
            {
              icon: Clock,
              title: "Lunch & Dinner",
              desc: "Daily thalis available for both meals. Breakfast on special order.",
            },
            {
              icon: MapPin,
              title: "Based in Delhi",
              desc: "Our home kitchen in Delhi cooks everything fresh, in small daily batches.",
            },
          ].map((tile, i) => {
            const Icon = tile.icon;
            return (
              <Reveal key={tile.title} delay={i + 1}>
                <div className="card-lift bg-[#faf8f5] rounded-2xl p-7 border border-[#c9a84c]/25 h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#1a2e5a] flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#c9a84c]" strokeWidth={1.8} />
                  </div>
                  <div className="font-display text-xl text-[#1a2e5a] font-semibold mb-2">
                    {tile.title}
                  </div>
                  <p className="font-body text-slate-600 text-sm leading-relaxed">
                    {tile.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Location */}
      <LocationSection variant="light" />

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white border-t border-[#c9a84c]/20" data-testid="contact-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-3">
                FAQ
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1a2e5a] leading-tight">
                Quick answers, <span className="italic text-[#c9a84c]">no fluff.</span>
              </h2>
            </Reveal>
          </div>

          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={(i % 5) + 1}>
                <details
                  className="group bg-[#faf8f5] rounded-2xl border border-[#c9a84c]/25 overflow-hidden"
                  data-testid={`faq-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-6">
                    <span className="font-display text-lg md:text-xl text-[#1a2e5a] font-semibold">
                      {f.q}
                    </span>
                    <span className="w-8 h-8 rounded-full bg-[#1a2e5a] text-[#c9a84c] flex items-center justify-center shrink-0 transition-transform duration-300 group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <div className="px-6 pb-6 -mt-2 font-body text-slate-600 leading-relaxed">
                    {f.a}
                  </div>
                </details>
              </Reveal>
            ))}
          </div>

          <Reveal delay={2}>
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-2 text-[#c9a84c] font-body text-sm mb-4">
                <Sparkles size={14} />
                Still have questions?
              </div>
              <div>
                <a
                  href={BRAND.whatsappChat}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  data-testid="faq-whatsapp-cta"
                >
                  <MessageCircle size={18} strokeWidth={2.5} />
                  Ask on WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
